import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Radio,
  Divider,
  CircularProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import ArticleIcon from '@mui/icons-material/Article';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import { useNavigate } from 'react-router-dom';
import { formatPrice as formatCurrencyPrice, formatMarketCap as formatCurrencyMarketCap, getCurrencyFromTicker } from '../utils/formatCurrency';
import { getTickers, saveTickers, getWatchlist, saveWatchlist } from '../utils/portfolioStorage';
import { bloomberg } from '../theme/bloombergTheme';
import {
  getModelPortfolios,
  getModelPortfolio,
  createModelPortfolio,
  setModelPortfolioHoldings,
  ModelPortfolioSummary,
} from '../services/api';

interface StockResult {
  symbol: string;
  name: string;
  price: number;
  change: number;
  change_percent: number;
  market_cap: number;
  pe_ratio: number;
  dividend_yield: number;
  beta?: number | null;
  analyst_rating?: number | null;
  // EDGAR-derived fundamentals
  ev_ebitda?: number | null;
  price_to_book?: number | null;
  price_to_sales?: number | null;
  roe?: number | null;
  fcf_yield?: number | null;
  debt_to_equity?: number | null;
  net_debt_to_ebitda?: number | null;
  volume?: number;
  avg_volume?: number;
  volume_ratio?: number | null;
  volume_change?: number | null;
  // Performance data (present in peer comparison mode)
  week_1?: number | null;
  month_1?: number | null;
  month_3?: number | null;
  ytd?: number | null;
  year_1?: number | null;
  sector: string;
  industry: string;
  region: string;
  currency?: string;
  // Technical analysis data (present when technical filters are applied)
  rsi?: number | null;
  sma_20?: number | null;
  sma_50?: number | null;
  sma_200?: number | null;
  pct_from_sma20?: number | null;
  pct_from_sma50?: number | null;
  pct_from_sma200?: number | null;
  high_52w?: number | null;
  low_52w?: number | null;
  pct_from_52w_high?: number | null;
  pct_from_52w_low?: number | null;
  is_ath?: boolean;
  is_atl?: boolean;
}

type ScreenerView = 'overview' | 'fundamentals' | 'technical' | 'performance' | 'volume';

type ColKey =
  | 'chg_pct' | 'mkt_cap' | 'sector' | 'region'
  | 'pe' | 'yield' | 'beta' | 'rating'
  | 'ev_ebitda' | 'pb' | 'ps' | 'roe' | 'fcf_y' | 'de' | 'nde'
  | 'rsi' | 'pct_20ma' | 'pct_50ma' | 'pct_200ma' | '52w_hi' | '52w_lo'
  | 'week1' | 'month1' | 'month3' | 'ytd' | 'year1'
  | 'volume' | 'vol_avg' | 'vol_chg';

const VIEW_COLUMNS: Record<ScreenerView, ColKey[]> = {
  overview:     ['chg_pct', 'mkt_cap', 'sector', 'region'],
  fundamentals: ['chg_pct', 'pe', 'ev_ebitda', 'pb', 'ps', 'roe', 'fcf_y', 'de', 'nde', 'yield', 'beta', 'rating'],
  technical:    ['chg_pct', 'rsi', 'pct_20ma', 'pct_50ma', 'pct_200ma', '52w_hi', '52w_lo'],
  performance:  ['chg_pct', 'week1', 'month1', 'month3', 'ytd', 'year1'],
  volume:       ['chg_pct', 'volume', 'vol_avg', 'vol_chg'],
};

const COL_LABEL: Record<ColKey, string> = {
  chg_pct: 'Chg %', mkt_cap: 'Mkt Cap', sector: 'Sector', region: 'Region',
  pe: 'P/E', yield: 'Yield', beta: 'Beta', rating: 'Rating',
  ev_ebitda: 'EV/EBITDA', pb: 'P/B', ps: 'P/S', roe: 'ROE %', fcf_y: 'FCF Y%',
  de: 'D/E', nde: 'NetD/EBITDA',
  rsi: 'RSI', pct_20ma: '% 20MA', pct_50ma: '% 50MA', pct_200ma: '% 200MA',
  '52w_hi': '52W Hi', '52w_lo': '52W Lo',
  week1: '1W', month1: '1M', month3: '3M', ytd: 'YTD', year1: '1Y',
  volume: 'Volume', vol_avg: 'Vol/Avg', vol_chg: 'Vol Chg%',
};

const COL_SORT_KEY: Partial<Record<ColKey, keyof StockResult>> = {
  chg_pct: 'change_percent', mkt_cap: 'market_cap', sector: 'sector', region: 'region',
  pe: 'pe_ratio', yield: 'dividend_yield', beta: 'beta', rating: 'analyst_rating',
  ev_ebitda: 'ev_ebitda', pb: 'price_to_book', ps: 'price_to_sales',
  roe: 'roe', fcf_y: 'fcf_yield', de: 'debt_to_equity', nde: 'net_debt_to_ebitda',
  rsi: 'rsi', pct_20ma: 'pct_from_sma20', pct_50ma: 'pct_from_sma50', pct_200ma: 'pct_from_sma200',
  '52w_hi': 'pct_from_52w_high', '52w_lo': 'pct_from_52w_low',
  week1: 'week_1', month1: 'month_1', month3: 'month_3', ytd: 'ytd', year1: 'year_1',
  volume: 'volume', vol_avg: 'volume_ratio', vol_chg: 'volume_change',
};

const COL_NUMERIC = new Set<ColKey>([
  'chg_pct', 'mkt_cap', 'pe', 'yield', 'beta', 'rating',
  'rsi', 'pct_20ma', 'pct_50ma', 'pct_200ma', '52w_hi', '52w_lo',
  'week1', 'month1', 'month3', 'ytd', 'year1',
  'volume', 'vol_avg', 'vol_chg',
]);

interface ScreenerResultsProps {
  results: StockResult[];
  loading: boolean;
  criteria: any;
  targetSymbol?: string | null;
  viewTab?: ScreenerView;
}

type OrderDirection = 'asc' | 'desc';

const ANALYTICS_TICKERS_KEY = 'analytics_tickers';

// Shared compact cell style
const CELL_SX = { fontSize: '0.6875rem', lineHeight: 1.3 } as const;
const HEADER_SX = { fontSize: '0.6875rem', fontWeight: 700, whiteSpace: 'nowrap', bgcolor: bloomberg.bg.tertiary, color: bloomberg.accent.orange } as const;

// Sector color map for dark theme
const SECTOR_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  'Technology':           { bg: 'rgba(59, 130, 246, 0.12)', text: '#60a5fa', border: 'rgba(59, 130, 246, 0.3)' },
  'Healthcare':           { bg: 'rgba(34, 197, 94, 0.12)', text: '#4ade80', border: 'rgba(34, 197, 94, 0.3)' },
  'Financial Services':   { bg: bloomberg.accent.orangeDim, text: bloomberg.accent.orange, border: bloomberg.border.accent },
  'Financials':           { bg: bloomberg.accent.orangeDim, text: bloomberg.accent.orange, border: bloomberg.border.accent },
  'Consumer Cyclical':    { bg: 'rgba(236, 72, 153, 0.12)', text: '#f472b6', border: 'rgba(236, 72, 153, 0.3)' },
  'Consumer Defensive':   { bg: 'rgba(168, 85, 247, 0.12)', text: '#c084fc', border: 'rgba(168, 85, 247, 0.3)' },
  'Industrials':          { bg: 'rgba(20, 184, 166, 0.12)', text: '#2dd4bf', border: 'rgba(20, 184, 166, 0.3)' },
  'Energy':               { bg: 'rgba(245, 158, 11, 0.12)', text: '#fbbf24', border: 'rgba(245, 158, 11, 0.3)' },
  'Utilities':            { bg: 'rgba(99, 102, 241, 0.12)', text: '#818cf8', border: 'rgba(99, 102, 241, 0.3)' },
  'Real Estate':          { bg: 'rgba(168, 85, 247, 0.12)', text: '#a78bfa', border: 'rgba(168, 85, 247, 0.3)' },
  'Basic Materials':      { bg: 'rgba(249, 115, 22, 0.12)', text: '#fb923c', border: 'rgba(249, 115, 22, 0.3)' },
  'Communication Services': { bg: 'rgba(6, 182, 212, 0.12)', text: '#22d3ee', border: 'rgba(6, 182, 212, 0.3)' },
  'Consumer Goods':       { bg: 'rgba(236, 72, 153, 0.12)', text: '#f472b6', border: 'rgba(236, 72, 153, 0.3)' },
};

const getSectorColor = (sector: string) => {
  return SECTOR_COLORS[sector] || { bg: 'rgba(100, 116, 139, 0.12)', text: '#94a3b8', border: 'rgba(100, 116, 139, 0.3)' };
};

const ScreenerResults: React.FC<ScreenerResultsProps> = ({ results, loading, criteria, targetSymbol, viewTab = 'overview' }) => {
  const navigate = useNavigate();
  const [orderBy, setOrderBy] = useState<keyof StockResult>('market_cap');
  const [order, setOrder] = useState<OrderDirection>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [addedSymbols, setAddedSymbols] = useState<Set<string>>(new Set());
  const [addedToWatchlist, setAddedToWatchlist] = useState<Set<string>>(new Set());
  const [addedToPortfolio, setAddedToPortfolio] = useState<Set<string>>(new Set());
  const [selectedSymbols, setSelectedSymbols] = useState<Set<string>>(new Set());
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Model Portfolio dialog state
  const [mpDialogOpen, setMpDialogOpen] = useState(false);
  const [mpDialogSymbols, setMpDialogSymbols] = useState<string[]>([]);
  const [mpList, setMpList] = useState<ModelPortfolioSummary[]>([]);
  const [mpLoading, setMpLoading] = useState(false);
  const [mpSaving, setMpSaving] = useState(false);
  const [mpSelectedId, setMpSelectedId] = useState<number | null>(null);
  const [mpMode, setMpMode] = useState<'existing' | 'new'>('existing');
  const [mpNewName, setMpNewName] = useState('');
  const [mpNewDesc, setMpNewDesc] = useState('');

  // Load existing symbols from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(ANALYTICS_TICKERS_KEY);
      if (stored) {
        const symbols = JSON.parse(stored);
        setAddedSymbols(new Set(symbols));
      }
      setAddedToWatchlist(new Set(getWatchlist()));
      setAddedToPortfolio(new Set(getTickers()));
    } catch (error) {
      console.error('Error loading analytics tickers:', error);
    }
  }, []);

  const handleAddToEfficientFrontier = (symbol: string) => {
    try {
      const stored = localStorage.getItem(ANALYTICS_TICKERS_KEY);
      const existingSymbols: string[] = stored ? JSON.parse(stored) : [];

      if (existingSymbols.includes(symbol)) {
        setSnackbar({ open: true, message: `${symbol} is already in Efficient Frontier analysis`, severity: 'error' });
        return;
      }

      const updatedSymbols = [...existingSymbols, symbol];
      localStorage.setItem(ANALYTICS_TICKERS_KEY, JSON.stringify(updatedSymbols));
      setAddedSymbols(new Set(updatedSymbols));
      setSnackbar({ open: true, message: `${symbol} added to Efficient Frontier analysis`, severity: 'success' });
    } catch (error) {
      console.error('Error adding symbol to efficient frontier:', error);
      setSnackbar({ open: true, message: `Failed to add ${symbol}`, severity: 'error' });
    }
  };

  const handleBulkAddToEfficientFrontier = () => {
    if (selectedSymbols.size === 0) {
      setSnackbar({ open: true, message: 'Please select at least one stock to add', severity: 'error' });
      return;
    }

    try {
      const stored = localStorage.getItem(ANALYTICS_TICKERS_KEY);
      const existingSymbols: string[] = stored ? JSON.parse(stored) : [];
      const symbolsToAdd = Array.from(selectedSymbols).filter(s => !existingSymbols.includes(s));

      if (symbolsToAdd.length === 0) {
        setSnackbar({ open: true, message: 'All selected stocks are already in Efficient Frontier analysis', severity: 'error' });
        return;
      }

      const updatedSymbols = [...existingSymbols, ...symbolsToAdd];
      localStorage.setItem(ANALYTICS_TICKERS_KEY, JSON.stringify(updatedSymbols));
      setAddedSymbols(new Set(updatedSymbols));
      setSelectedSymbols(new Set());
      setSnackbar({ open: true, message: `${symbolsToAdd.length} stock(s) added to Efficient Frontier analysis`, severity: 'success' });
    } catch (error) {
      console.error('Error bulk adding symbols to efficient frontier:', error);
      setSnackbar({ open: true, message: 'Failed to add stocks', severity: 'error' });
    }
  };

  const handleAddToWatchlist = (symbol: string) => {
    const current = getWatchlist();
    if (current.includes(symbol)) {
      setSnackbar({ open: true, message: `${symbol} is already in watchlist`, severity: 'error' });
      return;
    }
    saveWatchlist([...current, symbol]);
    setAddedToWatchlist(prev => new Set(prev).add(symbol));
    setSnackbar({ open: true, message: `${symbol} added to watchlist`, severity: 'success' });
  };

  const handleAddToPortfolio = (symbol: string) => {
    // Open dialog for single stock
    setMpDialogSymbols([symbol]);
    openMpDialog();
  };

  const handleBulkAddToWatchlist = () => {
    if (selectedSymbols.size === 0) {
      setSnackbar({ open: true, message: 'Select stocks first', severity: 'error' });
      return;
    }
    const current = getWatchlist();
    const toAdd = Array.from(selectedSymbols).filter(s => !current.includes(s));
    if (toAdd.length === 0) {
      setSnackbar({ open: true, message: 'All selected stocks already in watchlist', severity: 'error' });
      return;
    }
    saveWatchlist([...current, ...toAdd]);
    setAddedToWatchlist(prev => { const n = new Set(prev); toAdd.forEach(s => n.add(s)); return n; });
    setSelectedSymbols(new Set());
    setSnackbar({ open: true, message: `${toAdd.length} stock(s) added to watchlist`, severity: 'success' });
  };

  const handleBulkAddToPortfolio = () => {
    if (selectedSymbols.size === 0) {
      setSnackbar({ open: true, message: 'Select stocks first', severity: 'error' });
      return;
    }
    setMpDialogSymbols(Array.from(selectedSymbols));
    openMpDialog();
  };

  const openMpDialog = useCallback(async () => {
    setMpDialogOpen(true);
    setMpSelectedId(null);
    setMpMode('existing');
    setMpNewName('');
    setMpNewDesc('');
    setMpLoading(true);
    try {
      const portfolios = await getModelPortfolios();
      setMpList(portfolios);
      if (portfolios.length === 0) setMpMode('new');
    } catch (e) {
      console.error('Error fetching portfolios:', e);
    } finally {
      setMpLoading(false);
    }
  }, []);

  const handleMpSave = async () => {
    setMpSaving(true);
    try {
      if (mpMode === 'new') {
        if (!mpNewName.trim()) {
          setSnackbar({ open: true, message: 'Enter a portfolio name', severity: 'error' });
          setMpSaving(false);
          return;
        }
        // Create new portfolio with equal-weighted holdings
        const equalWeight = 1.0 / mpDialogSymbols.length;
        const portfolio = await createModelPortfolio(mpNewName.trim(), mpNewDesc.trim() || undefined);
        await setModelPortfolioHoldings(
          portfolio.id,
          mpDialogSymbols.map(s => ({ symbol: s, target_weight: equalWeight }))
        );
        setSnackbar({
          open: true,
          message: `Created "${mpNewName}" with ${mpDialogSymbols.length} stock(s) (equal-weighted)`,
          severity: 'success'
        });
      } else {
        if (!mpSelectedId) {
          setSnackbar({ open: true, message: 'Select a portfolio', severity: 'error' });
          setMpSaving(false);
          return;
        }
        // Fetch existing holdings, merge new stocks
        const detail = await getModelPortfolio(mpSelectedId);
        const existingSymbols = new Set(detail.holdings.map(h => h.symbol));
        const newSymbols = mpDialogSymbols.filter(s => !existingSymbols.has(s));

        if (newSymbols.length === 0) {
          setSnackbar({ open: true, message: 'All selected stocks already in this portfolio', severity: 'error' });
          setMpSaving(false);
          return;
        }

        // Redistribute weights: scale existing down proportionally, give equal share to new stocks
        const totalHoldings = detail.holdings.length + newSymbols.length;
        const newStockWeight = 1.0 / totalHoldings;
        const existingScale = (1.0 - newStockWeight * newSymbols.length) / (detail.total_weight || 1);

        const updatedHoldings = [
          ...detail.holdings.map(h => ({
            symbol: h.symbol,
            target_weight: h.target_weight * existingScale,
          })),
          ...newSymbols.map(s => ({
            symbol: s,
            target_weight: newStockWeight,
          })),
        ];

        await setModelPortfolioHoldings(mpSelectedId, updatedHoldings);
        const portfolioName = mpList.find(p => p.id === mpSelectedId)?.name || 'portfolio';
        setSnackbar({
          open: true,
          message: `Added ${newSymbols.length} stock(s) to "${portfolioName}" — weights rebalanced`,
          severity: 'success'
        });
      }

      setAddedToPortfolio(prev => {
        const n = new Set(prev);
        mpDialogSymbols.forEach(s => n.add(s));
        return n;
      });
      setSelectedSymbols(new Set());
      setMpDialogOpen(false);
    } catch (e: any) {
      console.error('Error saving to model portfolio:', e);
      setSnackbar({ open: true, message: e?.response?.data?.detail || 'Failed to save', severity: 'error' });
    } finally {
      setMpSaving(false);
    }
  };

  const handleToggleSelect = (symbol: string) => {
    setSelectedSymbols(prev => {
      const newSet = new Set(prev);
      if (newSet.has(symbol)) { newSet.delete(symbol); } else { newSet.add(symbol); }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedSymbols.size === paginatedResults.length) {
      setSelectedSymbols(new Set());
    } else {
      setSelectedSymbols(new Set(paginatedResults.map(r => r.symbol)));
    }
  };

  const handleSort = (property: keyof StockResult) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedResults = React.useMemo(() => {
    return [...results].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  }, [results, order, orderBy]);

  const handleChangePage = (_: unknown, newPage: number) => { setPage(newPage); };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => { setPage(0); }, [results.length]);

  const paginatedResults = React.useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedResults.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedResults, page, rowsPerPage]);

  const renderHeader = (col: ColKey): React.ReactNode => {
    const sortKey = COL_SORT_KEY[col];
    const isNum = COL_NUMERIC.has(col);
    return (
      <TableCell key={col} align={isNum ? 'right' : 'left'}>
        {sortKey ? (
          <TableSortLabel
            active={orderBy === sortKey}
            direction={orderBy === sortKey ? order : 'asc'}
            onClick={() => handleSort(sortKey)}
          >
            {COL_LABEL[col]}
          </TableSortLabel>
        ) : COL_LABEL[col]}
      </TableCell>
    );
  };

  const renderCell = (col: ColKey, stock: StockResult): React.ReactNode => {
    const pct = (v: number | null | undefined, invert = false) => {
      if (v == null) return <Typography sx={CELL_SX} color="text.secondary">-</Typography>;
      const sign = invert ? (v <= 0 ? '' : '+') : (v >= 0 ? '+' : '');
      const color = invert
        ? (v <= -5 ? 'success.main' : 'text.primary')
        : (v >= 0 ? 'success.main' : 'error.main');
      return <Typography sx={{ ...CELL_SX, color }}>{sign}{(invert ? -v : v).toFixed(1)}%</Typography>;
    };
    switch (col) {
      case 'chg_pct': return (
        <TableCell key={col} align="right">
          <Typography sx={{ ...CELL_SX, color: stock.change_percent > 0 ? 'success.main' : stock.change_percent < 0 ? 'error.main' : 'text.primary' }}>
            {stock.change_percent > 0 ? '+' : ''}{stock.change_percent.toFixed(2)}%
          </Typography>
        </TableCell>
      );
      case 'mkt_cap': return (
        <TableCell key={col} align="right">
          <Typography sx={CELL_SX}>{formatMarketCap(stock.market_cap, stock.currency || getCurrencyFromTicker(stock.symbol))}</Typography>
        </TableCell>
      );
      case 'sector': return (
        <TableCell key={col}>
          <Box>
            {(() => { const sc = getSectorColor(stock.sector); return (
              <Chip label={stock.sector || 'N/A'} size="small" sx={{ height: 18, fontSize: '0.6rem', mb: 0.25, bgcolor: sc.bg, color: sc.text, borderColor: sc.border, border: '1px solid', fontWeight: 600 }} />
            ); })()}
            {stock.industry && stock.industry !== 'Unknown' && (
              <Chip label={stock.industry} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.55rem', display: 'flex', maxWidth: 'fit-content', color: 'text.secondary', borderColor: 'divider' }} />
            )}
          </Box>
        </TableCell>
      );
      case 'region': return <TableCell key={col}><Typography sx={CELL_SX}>{stock.region}</Typography></TableCell>;
      case 'pe': return <TableCell key={col} align="right"><Typography sx={CELL_SX}>{stock.pe_ratio && stock.pe_ratio > 0 ? stock.pe_ratio.toFixed(1) : 'N/A'}</Typography></TableCell>;
      case 'yield': return <TableCell key={col} align="right"><Typography sx={CELL_SX}>{stock.dividend_yield ? `${stock.dividend_yield.toFixed(2)}%` : 'N/A'}</Typography></TableCell>;
      case 'ev_ebitda': return <TableCell key={col} align="right"><Typography sx={CELL_SX}>{stock.ev_ebitda != null ? stock.ev_ebitda.toFixed(1) : '—'}</Typography></TableCell>;
      case 'pb': return <TableCell key={col} align="right"><Typography sx={CELL_SX}>{stock.price_to_book != null ? stock.price_to_book.toFixed(2) : '—'}</Typography></TableCell>;
      case 'ps': return <TableCell key={col} align="right"><Typography sx={CELL_SX}>{stock.price_to_sales != null ? stock.price_to_sales.toFixed(2) : '—'}</Typography></TableCell>;
      case 'roe': return (
        <TableCell key={col} align="right">
          <Typography sx={{ ...CELL_SX, fontWeight: 500, color: stock.roe != null ? (stock.roe >= 20 ? 'success.main' : stock.roe < 0 ? 'error.main' : 'text.primary') : 'text.secondary' }}>
            {stock.roe != null ? `${stock.roe.toFixed(1)}%` : '—'}
          </Typography>
        </TableCell>
      );
      case 'fcf_y': return (
        <TableCell key={col} align="right">
          <Typography sx={{ ...CELL_SX, fontWeight: 500, color: stock.fcf_yield != null ? (stock.fcf_yield >= 5 ? 'success.main' : stock.fcf_yield < 0 ? 'error.main' : 'text.primary') : 'text.secondary' }}>
            {stock.fcf_yield != null ? `${stock.fcf_yield.toFixed(1)}%` : '—'}
          </Typography>
        </TableCell>
      );
      case 'de': return (
        <TableCell key={col} align="right">
          <Typography sx={{ ...CELL_SX, fontWeight: 500, color: stock.debt_to_equity != null ? (stock.debt_to_equity > 1.5 ? 'error.main' : stock.debt_to_equity < 0.5 ? 'success.main' : 'text.primary') : 'text.secondary' }}>
            {stock.debt_to_equity != null ? stock.debt_to_equity.toFixed(2) : '—'}
          </Typography>
        </TableCell>
      );
      case 'nde': return (
        <TableCell key={col} align="right">
          <Typography sx={{ ...CELL_SX, fontWeight: 500, color: stock.net_debt_to_ebitda != null ? (stock.net_debt_to_ebitda > 4 ? 'error.main' : stock.net_debt_to_ebitda < 0 ? 'info.main' : 'text.primary') : 'text.secondary' }}>
            {stock.net_debt_to_ebitda != null ? stock.net_debt_to_ebitda.toFixed(2) : '—'}
          </Typography>
        </TableCell>
      );
      case 'beta': return (
        <TableCell key={col} align="right">
          <Typography sx={{ ...CELL_SX, fontWeight: 500, color: stock.beta != null ? (stock.beta > 1.5 ? 'error.main' : stock.beta < 0.8 ? 'info.main' : 'text.primary') : 'text.secondary' }}>
            {stock.beta != null ? stock.beta.toFixed(2) : '-'}
          </Typography>
        </TableCell>
      );
      case 'rating': return (
        <TableCell key={col} align="right">
          <Typography sx={{ ...CELL_SX, fontWeight: 500, color: stock.analyst_rating != null ? (stock.analyst_rating >= 4.5 ? '#4caf50' : stock.analyst_rating >= 3.5 ? '#8bc34a' : stock.analyst_rating >= 2.5 ? '#ff9800' : '#f44336') : 'text.secondary' }}>
            {stock.analyst_rating != null ? stock.analyst_rating.toFixed(1) : '-'}
          </Typography>
        </TableCell>
      );
      case 'rsi': return (
        <TableCell key={col} align="right">
          {stock.rsi != null
            ? <Typography sx={{ ...CELL_SX, fontWeight: 500, color: stock.rsi >= 70 ? 'error.main' : stock.rsi <= 30 ? 'success.main' : 'text.primary' }}>{stock.rsi.toFixed(1)}</Typography>
            : <Typography sx={CELL_SX} color="text.secondary">-</Typography>}
        </TableCell>
      );
      case 'pct_20ma': return <TableCell key={col} align="right">{pct(stock.pct_from_sma20)}</TableCell>;
      case 'pct_50ma': return <TableCell key={col} align="right">{pct(stock.pct_from_sma50)}</TableCell>;
      case 'pct_200ma': return <TableCell key={col} align="right">{pct(stock.pct_from_sma200)}</TableCell>;
      case '52w_hi': return (
        <TableCell key={col} align="right">
          {stock.pct_from_52w_high != null
            ? <Typography sx={{ ...CELL_SX, color: stock.pct_from_52w_high <= 5 ? 'success.main' : 'text.primary' }}>-{stock.pct_from_52w_high.toFixed(1)}%</Typography>
            : <Typography sx={CELL_SX} color="text.secondary">-</Typography>}
        </TableCell>
      );
      case '52w_lo': return (
        <TableCell key={col} align="right">
          {stock.pct_from_52w_low != null
            ? <Typography sx={{ ...CELL_SX, color: stock.pct_from_52w_low <= 10 ? 'warning.main' : 'text.primary' }}>+{stock.pct_from_52w_low.toFixed(1)}%</Typography>
            : <Typography sx={CELL_SX} color="text.secondary">-</Typography>}
        </TableCell>
      );
      case 'week1': return <TableCell key={col} align="right">{pct(stock.week_1)}</TableCell>;
      case 'month1': return <TableCell key={col} align="right">{pct(stock.month_1)}</TableCell>;
      case 'month3': return <TableCell key={col} align="right">{pct(stock.month_3)}</TableCell>;
      case 'ytd': return <TableCell key={col} align="right">{pct(stock.ytd)}</TableCell>;
      case 'year1': return <TableCell key={col} align="right">{pct(stock.year_1)}</TableCell>;
      case 'volume': return <TableCell key={col} align="right"><Typography sx={CELL_SX}>{formatVolume(stock.volume)}</Typography></TableCell>;
      case 'vol_avg': return (
        <TableCell key={col} align="right">
          <Typography sx={{ ...CELL_SX, fontWeight: 500, color: stock.volume_ratio != null ? (stock.volume_ratio >= 2 ? '#4caf50' : stock.volume_ratio < 0.5 ? '#f44336' : 'text.primary') : 'text.secondary' }}>
            {stock.volume_ratio != null ? `${stock.volume_ratio.toFixed(1)}x` : '-'}
          </Typography>
        </TableCell>
      );
      case 'vol_chg': return (
        <TableCell key={col} align="right">
          <Typography sx={{ ...CELL_SX, fontWeight: 500, color: stock.volume_change != null ? (stock.volume_change > 0 ? '#4caf50' : '#f44336') : 'text.secondary' }}>
            {stock.volume_change != null ? `${stock.volume_change > 0 ? '+' : ''}${stock.volume_change.toFixed(1)}%` : '-'}
          </Typography>
        </TableCell>
      );
      default: return <TableCell key={col} />;
    }
  };

  const formatNumber = (num: number, decimals: number = 2): string => {
    if (!num && num !== 0) return 'N/A';
    return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatMarketCap = (value: number, currency: string = 'USD'): string => {
    return formatCurrencyMarketCap(value, currency);
  };

  const formatVolume = (vol: number | undefined): string => {
    if (!vol) return '-';
    if (vol >= 1_000_000_000) return `${(vol / 1_000_000_000).toFixed(1)}B`;
    if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(1)}M`;
    if (vol >= 1_000) return `${(vol / 1_000).toFixed(0)}K`;
    return vol.toLocaleString();
  };

  const handleExport = () => {
    const headers = [
      'Symbol', 'Name', 'Price', 'Change %', 'Market Cap', 'P/E', 'Div Yield %', 'Beta', 'Rating',
      '1W %', '1M %', '3M %', 'YTD %', '1Y %',
      'Volume', 'Avg Volume', 'Vol/Avg', 'Vol Chg%',
      'Sector', 'Industry', 'Region',
      'RSI', '% 20MA', '% 50MA', '% 200MA', '52W Hi', '52W Lo'
    ];

    const csvContent = [
      headers.join(','),
      ...sortedResults.map(stock =>
        [
          stock.symbol,
          `"${stock.name}"`,
          stock.price,
          stock.change_percent,
          stock.market_cap,
          stock.pe_ratio || 'N/A',
          stock.dividend_yield ? stock.dividend_yield.toFixed(2) : '0',
          stock.beta != null ? stock.beta.toFixed(2) : '',
          stock.analyst_rating != null ? stock.analyst_rating.toFixed(1) : '',
          (stock as any).week_1 != null ? (stock as any).week_1.toFixed(2) : '',
          (stock as any).month_1 != null ? (stock as any).month_1.toFixed(2) : '',
          (stock as any).month_3 != null ? (stock as any).month_3.toFixed(2) : '',
          (stock as any).ytd != null ? (stock as any).ytd.toFixed(2) : '',
          (stock as any).year_1 != null ? (stock as any).year_1.toFixed(2) : '',
          stock.volume || 0,
          stock.avg_volume || 0,
          stock.volume_ratio != null ? stock.volume_ratio.toFixed(2) : '',
          stock.volume_change != null ? stock.volume_change.toFixed(2) : '',
          `"${stock.sector}"`,
          `"${stock.industry}"`,
          stock.region,
          stock.rsi != null ? stock.rsi.toFixed(2) : '',
          stock.pct_from_sma20 != null ? stock.pct_from_sma20.toFixed(2) : '',
          stock.pct_from_sma50 != null ? stock.pct_from_sma50.toFixed(2) : '',
          stock.pct_from_sma200 != null ? stock.pct_from_sma200.toFixed(2) : '',
          stock.pct_from_52w_high != null ? stock.pct_from_52w_high.toFixed(2) : '',
          stock.pct_from_52w_low != null ? stock.pct_from_52w_low.toFixed(2) : '',
        ].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `screener_results_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (results.length === 0 && !loading) {
    return (
      <Alert severity="info">
        No stocks found matching your criteria. Try adjusting your filters.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Results ({results.length})
          </Typography>
          {selectedSymbols.size > 0 && (
            <>
              <Button
                variant="contained"
                size="small"
                onClick={handleBulkAddToEfficientFrontier}
                startIcon={<AddCircleIcon sx={{ fontSize: 14 }} />}
                sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
              >
                Add {selectedSymbols.size} to EF
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleBulkAddToWatchlist}
                startIcon={<BookmarkAddIcon sx={{ fontSize: 14 }} />}
                sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
              >
                Watchlist
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleBulkAddToPortfolio}
                startIcon={<FolderSpecialIcon sx={{ fontSize: 14 }} />}
                sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
              >
                Model Portfolio
              </Button>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Per Page</InputLabel>
            <Select
              value={rowsPerPage}
              label="Per Page"
              onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
              sx={{ fontSize: '0.75rem' }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Export to CSV">
            <IconButton onClick={handleExport} size="small" disabled={results.length === 0}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table
          size="small"
          stickyHeader
          sx={{
            '& .MuiTableCell-root': { px: 1, py: 0.5 },
            '& .MuiTableCell-head': HEADER_SX,
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 40 }}>
                <Checkbox
                  indeterminate={selectedSymbols.size > 0 && selectedSymbols.size < paginatedResults.length}
                  checked={paginatedResults.length > 0 && selectedSymbols.size === paginatedResults.length}
                  onChange={handleSelectAll}
                  size="small"
                  sx={{ p: 0.25 }}
                />
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'symbol'} direction={orderBy === 'symbol' ? order : 'asc'} onClick={() => handleSort('symbol')}>
                  Symbol
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={() => handleSort('name')}>
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel active={orderBy === 'price'} direction={orderBy === 'price' ? order : 'asc'} onClick={() => handleSort('price')}>
                  Price
                </TableSortLabel>
              </TableCell>
              {VIEW_COLUMNS[viewTab].map(col => renderHeader(col))}
              <TableCell align="center" sx={{ width: 80 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedResults.map((stock) => (
              <TableRow
                key={stock.symbol}
                hover
                sx={{
                  cursor: 'default',
                  '&:hover .row-actions': { visibility: 'visible' },
                  bgcolor: selectedSymbols.has(stock.symbol)
                    ? 'action.selected'
                    : targetSymbol && stock.symbol === targetSymbol
                      ? 'rgba(103, 58, 183, 0.08)'
                      : 'inherit',
                  borderLeft: targetSymbol && stock.symbol === targetSymbol ? '3px solid #673ab7' : 'none',
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedSymbols.has(stock.symbol)}
                    onChange={() => handleToggleSelect(stock.symbol)}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    sx={{ p: 0.25 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{ ...CELL_SX, fontWeight: 600, color: bloomberg.accent.orange, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={e => { e.stopPropagation(); navigate(`/stock/${stock.symbol}`); }}
                  >
                    {stock.symbol}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography noWrap sx={{ ...CELL_SX, maxWidth: 160 }}>{stock.name}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={CELL_SX}>
                    {formatCurrencyPrice(stock.price, stock.currency || getCurrencyFromTicker(stock.symbol))}
                  </Typography>
                </TableCell>
                {VIEW_COLUMNS[viewTab].map(col => renderCell(col, stock))}
                <TableCell
                  className="row-actions"
                  align="center"
                  sx={{ visibility: 'hidden', width: 80, whiteSpace: 'nowrap' }}
                >
                  <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center' }}>
                    <Tooltip title={addedSymbols.has(stock.symbol) ? "Already in EF" : "Add to EF"}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleAddToEfficientFrontier(stock.symbol); }} disabled={addedSymbols.has(stock.symbol)} color={addedSymbols.has(stock.symbol) ? "success" : "primary"} sx={{ p: 0.25 }}>
                        {addedSymbols.has(stock.symbol) ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <AddCircleIcon sx={{ fontSize: 14 }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={addedToWatchlist.has(stock.symbol) ? "In watchlist" : "Add to watchlist"}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleAddToWatchlist(stock.symbol); }} disabled={addedToWatchlist.has(stock.symbol)} sx={{ p: 0.25, color: addedToWatchlist.has(stock.symbol) ? '#22c55e' : bloomberg.accent.orange }}>
                        {addedToWatchlist.has(stock.symbol) ? <BookmarkAddedIcon sx={{ fontSize: 14 }} /> : <BookmarkAddIcon sx={{ fontSize: 14 }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={addedToPortfolio.has(stock.symbol) ? "In portfolio" : "Add to portfolio"}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleAddToPortfolio(stock.symbol); }} disabled={addedToPortfolio.has(stock.symbol)} sx={{ p: 0.25, color: addedToPortfolio.has(stock.symbol) ? '#22c55e' : '#3b82f6' }}>
                        {addedToPortfolio.has(stock.symbol) ? <FolderSpecialIcon sx={{ fontSize: 14 }} /> : <PlaylistAddIcon sx={{ fontSize: 14 }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Research">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/research?symbol=${stock.symbol}`); }} sx={{ p: 0.25, color: '#a855f7' }}>
                        <ArticleIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <TablePagination
        component="div"
        count={results.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 50, 100]}
        labelRowsPerPage="Rows:"
        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' } }}
      />

      {results.length >= (criteria.max_results || 100) && (
        <Alert severity="info" sx={{ mt: 1, fontSize: '0.75rem' }}>
          Showing maximum {criteria.max_results || 100} results. Refine your criteria for more specific results.
        </Alert>
      )}

      {/* Model Portfolio Dialog */}
      <Dialog
        open={mpDialogOpen}
        onClose={() => setMpDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: bloomberg.bg.secondary, color: bloomberg.text.primary } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: `2px solid ${bloomberg.accent.orange}` }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: bloomberg.accent.orange }}>
            Add {mpDialogSymbols.length} Stock{mpDialogSymbols.length !== 1 ? 's' : ''} to Model Portfolio
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
            {mpDialogSymbols.slice(0, 10).map(s => (
              <Chip key={s} label={s} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: bloomberg.bg.tertiary, color: bloomberg.text.primary }} />
            ))}
            {mpDialogSymbols.length > 10 && (
              <Chip label={`+${mpDialogSymbols.length - 10} more`} size="small" sx={{ height: 20, fontSize: 10, color: bloomberg.text.muted }} />
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          {mpLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
          ) : (
            <>
              {/* Toggle: Existing vs New */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant={mpMode === 'existing' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setMpMode('existing')}
                  disabled={mpList.length === 0}
                  sx={{ fontSize: 11, flex: 1 }}
                >
                  Add to Existing
                </Button>
                <Button
                  variant={mpMode === 'new' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setMpMode('new')}
                  startIcon={<CreateNewFolderIcon sx={{ fontSize: 14 }} />}
                  sx={{ fontSize: 11, flex: 1 }}
                >
                  Create New Portfolio
                </Button>
              </Box>

              {mpMode === 'existing' ? (
                <List dense sx={{ maxHeight: 280, overflow: 'auto', bgcolor: bloomberg.bg.primary, borderRadius: 1 }}>
                  {mpList.map(p => (
                    <ListItemButton
                      key={p.id}
                      selected={mpSelectedId === p.id}
                      onClick={() => setMpSelectedId(p.id)}
                      sx={{
                        borderBottom: `1px solid ${bloomberg.border.primary}`,
                        '&.Mui-selected': { bgcolor: bloomberg.accent.orangeDim },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Radio checked={mpSelectedId === p.id} size="small" sx={{ p: 0.25, color: bloomberg.accent.orange, '&.Mui-checked': { color: bloomberg.accent.orange } }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={p.name}
                        secondary={`${p.holdings_count} holdings · ${(p.total_weight * 100).toFixed(0)}% allocated`}
                        primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                        secondaryTypographyProps={{ fontSize: 11, color: bloomberg.text.muted }}
                      />
                    </ListItemButton>
                  ))}
                  {mpList.length === 0 && (
                    <Typography variant="body2" sx={{ p: 2, color: bloomberg.text.muted, textAlign: 'center' }}>
                      No model portfolios yet. Create one instead.
                    </Typography>
                  )}
                </List>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Portfolio Name"
                    value={mpNewName}
                    onChange={e => setMpNewName(e.target.value)}
                    size="small"
                    fullWidth
                    autoFocus
                    placeholder="e.g. Tech Growth, Dividend Income"
                    InputProps={{ sx: { fontSize: 13 } }}
                    InputLabelProps={{ sx: { fontSize: 13 } }}
                  />
                  <TextField
                    label="Description (optional)"
                    value={mpNewDesc}
                    onChange={e => setMpNewDesc(e.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Portfolio strategy or notes"
                    InputProps={{ sx: { fontSize: 13 } }}
                    InputLabelProps={{ sx: { fontSize: 13 } }}
                  />
                  <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>
                    {mpDialogSymbols.length} stock{mpDialogSymbols.length !== 1 ? 's' : ''} will be added with equal weights ({(100 / mpDialogSymbols.length).toFixed(1)}% each)
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: `1px solid ${bloomberg.border.primary}` }}>
          <Button onClick={() => setMpDialogOpen(false)} size="small" sx={{ fontSize: 12, color: bloomberg.text.muted }}>
            Cancel
          </Button>
          <Button
            onClick={handleMpSave}
            variant="contained"
            size="small"
            disabled={mpSaving || (mpMode === 'existing' && !mpSelectedId) || (mpMode === 'new' && !mpNewName.trim())}
            sx={{ fontSize: 12, bgcolor: bloomberg.accent.orange, '&:hover': { bgcolor: '#e68a00' } }}
          >
            {mpSaving ? <CircularProgress size={16} /> : mpMode === 'new' ? 'Create & Add' : 'Add to Portfolio'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScreenerResults;
